/**
 * Chat history inside the widget: search, day-grouped rows, per-row rename /
 * pin / delete, and a link out to the full /alfie page.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowUpRight,
  ChevronLeft,
  Loader2,
  MoreHorizontal,
  Pencil,
  Pin,
  PinOff,
  Plus,
  Search,
  Trash2,
} from 'lucide-react';
import { Link } from '@tanstack/react-router';

import { trackWidget } from './widget-analytics';

import {
  useArchiveThread,
  useRenameThread,
  useSetThreadPinned,
  useThreadSearch,
  useThreadsPaged,
  type Thread,
} from '@/api/alfie';
import { useIsMobile } from '@/hooks/use-mobile';

import { groupThreads, relativeTime } from './thread-groups';

const LONG_PRESS_MS = 500;
/** Keystroke settle time before a search hits the server. */
const SEARCH_DEBOUNCE_MS = 250;
/** Matches the `alfie_threads.title` column's own ceiling. */
const MAX_TITLE_LENGTH = 200;

const OVERLINE = 'text-[11px] font-semibold uppercase leading-[14px] tracking-[0.06em] text-muted-foreground';

interface WidgetHistoryViewProps {
  activeThreadId: string | null;
  onSelect: (thread: Thread) => void;
  onNewChat: () => void;
  onBack: () => void;
  onNavigateAway: () => void;
  /** The open conversation was just deleted — unbind it, but stay on the list. */
  onActiveThreadDeleted: () => void;
}

type RowMode = 'idle' | 'menu' | 'rename' | 'confirm-delete';

export function WidgetHistoryView({
  activeThreadId,
  onSelect,
  onNewChat,
  onBack,
  onNavigateAway,
  onActiveThreadDeleted,
}: WidgetHistoryViewProps) {
  const isMobile = useIsMobile();
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [openRowId, setOpenRowId] = useState<string | null>(null);
  const [rowMode, setRowMode] = useState<RowMode>('idle');
  const [renameValue, setRenameValue] = useState('');
  // Set by Escape so the blur React fires as the rename input unmounts cannot
  // commit the text the user just discarded.
  const renameCancelled = useRef(false);

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useThreadsPaged();
  const rename = useRenameThread();
  const archive = useArchiveThread();
  const setPinned = useSetThreadPinned();

  const needle = query.trim();
  const isSearching = needle.length > 0;

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedQuery(needle), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [needle]);

  // Server-side: a client-side filter only ever searched the pages already
  // fetched, so a match further down the list was invisible until the user
  // pressed "Load more" enough times to reach it.
  const search = useThreadSearch(debouncedQuery);

  const threads = useMemo(() => data?.pages.flat() ?? [], [data]);
  const rows = isSearching ? search.data ?? [] : threads;
  // Anything short of a settled server answer is a load, not an empty result:
  // the query is disabled through the debounce window, and "no chats match"
  // flashed mid-keystroke reads as a wrong answer.
  const rowsLoading = isSearching ? !search.isSuccess : isLoading;

  const groups = useMemo(() => groupThreads(rows), [rows]);

  const closeRow = useCallback(() => {
    setOpenRowId(null);
    setRowMode('idle');
  }, []);

  // Outside-dismiss binds pointerdown, never mousedown: on touch, mousedown is
  // synthesised ~300ms later (or not at all), which leaves the menu stuck open.
  useEffect(() => {
    if (rowMode !== 'menu') return;
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as HTMLElement | null;
      if (target?.closest('[data-row-menu]')) return;
      closeRow();
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [rowMode, closeRow]);

  // Escape backs out of the row's menu / rename box / delete confirm. Bound in
  // the CAPTURE phase because the panel also listens for Escape on `document`
  // and would otherwise dismiss the whole widget on the same keypress.
  useEffect(() => {
    if (rowMode === 'idle') return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopPropagation();
      if (rowMode === 'rename') renameCancelled.current = true;
      closeRow();
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [rowMode, closeRow]);

  const openMenu = (thread: Thread) => {
    setOpenRowId(thread.id);
    setRowMode('menu');
  };

  /** The "…" is a toggle: a second click on the row whose menu is already open
   *  closes it, instead of re-opening the menu the user just dismissed. */
  const toggleMenu = (thread: Thread) => {
    if (openRowId === thread.id && rowMode === 'menu') {
      closeRow();
      return;
    }
    openMenu(thread);
  };

  const startRename = (thread: Thread) => {
    renameCancelled.current = false;
    setOpenRowId(thread.id);
    setRenameValue(thread.title ?? '');
    setRowMode('rename');
  };

  const commitRename = (thread: Thread) => {
    if (renameCancelled.current) return;
    const title = renameValue.trim();
    if (title && title !== thread.title) {
      rename.mutate({ threadId: thread.id, title });
      trackWidget({ action: 'thread_renamed', threadId: thread.id });
    }
    closeRow();
  };

  const rowHeight = isMobile ? 'h-14' : 'h-[52px]';
  const titleSize = isMobile ? 'text-[15px]' : 'text-sm';
  const metaSize = isMobile ? 'text-[13px]' : 'text-xs';

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Header */}
      <div className="flex h-[60px] shrink-0 items-center gap-1 border-b border-border pl-1 pr-3">
        <button
          type="button"
          onClick={onBack}
          aria-label="Back to chat"
          className={`flex shrink-0 items-center justify-center rounded-md text-foreground transition-colors hover:bg-foreground/[0.06] ${
            isMobile ? 'h-11 w-11' : 'h-9 w-9'
          }`}
        >
          <ChevronLeft className={isMobile ? 'h-[22px] w-[22px]' : 'h-5 w-5'} strokeWidth={1.75} />
        </button>
        <h2
          className={`flex-1 font-semibold text-foreground ${
            isMobile ? 'text-[17px] leading-[22px]' : 'text-[15px] leading-[18px]'
          }`}
        >
          Chats
        </h2>
        <button
          type="button"
          onClick={onNewChat}
          className={`flex shrink-0 items-center gap-1.5 rounded-full bg-primary pl-2.5 pr-3.5 font-semibold text-primary-foreground transition-colors hover:bg-primary/90 ${
            isMobile ? 'h-[38px] text-sm' : 'h-[34px] text-[13px]'
          }`}
        >
          <Plus className="h-4 w-4" strokeWidth={2} />
          New chat
        </button>
      </div>

      {/* Search */}
      <div className="shrink-0 px-4 pb-1 pt-3">
        <div
          className={`flex items-center gap-2 rounded-lg border border-border bg-muted px-3 ${
            isMobile ? 'h-11' : 'h-10'
          }`}
        >
          <Search className="h-4 w-4 shrink-0 text-muted-foreground" strokeWidth={2} />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search chats"
            aria-label="Search chats"
            className="min-w-0 flex-1 bg-transparent text-base leading-[20px] text-foreground outline-none placeholder:text-muted-foreground sm:text-sm sm:leading-[18px]"
          />
        </div>
      </div>

      {/* List */}
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-2 pt-2">
        {rowsLoading && (
          <div className="space-y-2 px-1 py-2" aria-hidden>
            {[0, 1, 2].map((i) => (
              <div key={i} className={`${rowHeight} animate-pulse rounded-xl bg-muted`} />
            ))}
          </div>
        )}

        {!rowsLoading && groups.length === 0 && (
          <p className="px-3 py-6 text-center text-sm text-muted-foreground">
            {isSearching ? 'No chats match that search.' : 'No chats yet.'}
          </p>
        )}

        {groups.map((group, groupIndex) => (
          <section key={group.key}>
            <div className={`px-3 pb-1.5 ${groupIndex === 0 ? 'pt-2.5' : 'pt-3.5'}`}>
              <h3 className={OVERLINE}>{group.label}</h3>
            </div>
            <ul>
              {group.threads.map((thread) => {
                const isOpen = openRowId === thread.id;
                const isActive = activeThreadId === thread.id;

                if (isOpen && rowMode === 'rename') {
                  return (
                    <li key={thread.id} className={`flex items-center px-3 ${rowHeight}`}>
                      <input
                        autoFocus
                        value={renameValue}
                        onChange={(e) => setRenameValue(e.target.value)}
                        onBlur={() => commitRename(thread)}
                        // Escape is handled by the capture-phase listener above,
                        // which has to beat the panel's own Escape binding.
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            commitRename(thread);
                          }
                        }}
                        aria-label="Chat title"
                        maxLength={MAX_TITLE_LENGTH}
                        className="w-full rounded-lg border border-primary bg-background px-2.5 py-1.5 text-base text-foreground outline-none sm:text-sm"
                      />
                    </li>
                  );
                }

                if (isOpen && rowMode === 'confirm-delete') {
                  return (
                    <li
                      key={thread.id}
                      className={`flex items-center gap-2 rounded-lg bg-muted px-3 ${rowHeight}`}
                    >
                      <span className="min-w-0 flex-1 truncate text-sm text-foreground">
                        Delete this chat?
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          archive.mutate({ threadId: thread.id });
                          trackWidget({ action: 'thread_deleted', threadId: thread.id });
                          // Deleting the chat you are IN must unbind it too —
                          // otherwise the widget keeps writing messages into an
                          // archived thread the user can no longer open.
                          if (activeThreadId === thread.id) onActiveThreadDeleted();
                          closeRow();
                        }}
                        className="rounded-lg bg-destructive px-3 py-1.5 text-xs font-semibold text-destructive-foreground"
                      >
                        Delete
                      </button>
                      <button
                        type="button"
                        onClick={closeRow}
                        className="rounded-lg px-2.5 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground"
                      >
                        Cancel
                      </button>
                    </li>
                  );
                }

                return (
                  <ThreadRow
                    key={thread.id}
                    thread={thread}
                    isActive={isActive}
                    isMenuOpen={isOpen && rowMode === 'menu'}
                    isMobile={isMobile}
                    rowHeight={rowHeight}
                    titleSize={titleSize}
                    metaSize={metaSize}
                    onSelect={() => onSelect(thread)}
                    onOpenMenu={() => openMenu(thread)}
                    onToggleMenu={() => toggleMenu(thread)}
                    onRename={() => startRename(thread)}
                    onTogglePin={() => {
                      setPinned.mutate({ threadId: thread.id, pinned: !thread.pinned_at });
                      trackWidget({
                        action: thread.pinned_at ? 'thread_unpinned' : 'thread_pinned',
                        threadId: thread.id,
                      });
                      closeRow();
                    }}
                    onDelete={() => setRowMode('confirm-delete')}
                  />
                );
              })}
            </ul>
          </section>
        ))}

        {hasNextPage && !isSearching && (
          <button
            type="button"
            onClick={() => void fetchNextPage()}
            disabled={isFetchingNextPage}
            className="my-2 flex h-11 w-full items-center justify-center gap-2 rounded-xl text-[13px] font-medium text-muted-foreground transition-colors hover:bg-foreground/[0.04] hover:text-foreground"
          >
            {isFetchingNextPage && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Load more
          </button>
        )}
      </div>

      {/* Footer */}
      <Link
        to="/alfie"
        onClick={onNavigateAway}
        className={`flex shrink-0 items-center justify-center gap-1.5 border-t border-border font-medium text-primary transition-colors hover:bg-foreground/[0.04] ${
          isMobile
            ? // The safe-area strip is part of the row's HEIGHT, not padding
              // eaten out of a fixed 56px — that left the label crowding the
              // border on any phone with a home indicator.
              'h-[calc(3.5rem+var(--safe-bottom))] text-sm'
            : 'h-12 text-[13px]'
        }`}
      >
        See all chats in Alfie
        <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={2} />
      </Link>
    </div>
  );
}

interface ThreadRowProps {
  thread: Thread;
  isActive: boolean;
  isMenuOpen: boolean;
  isMobile: boolean;
  rowHeight: string;
  titleSize: string;
  metaSize: string;
  onSelect: () => void;
  /** Long-press only — always opens. */
  onOpenMenu: () => void;
  /** The "…" button — opens, or closes its own open menu. */
  onToggleMenu: () => void;
  onRename: () => void;
  onTogglePin: () => void;
  onDelete: () => void;
}

function ThreadRow({
  thread,
  isActive,
  isMenuOpen,
  isMobile,
  rowHeight,
  titleSize,
  metaSize,
  onSelect,
  onOpenMenu,
  onToggleMenu,
  onRename,
  onTogglePin,
  onDelete,
}: ThreadRowProps) {
  const holdTimer = useRef<ReturnType<typeof setTimeout>>();
  // Set when a long-press opened the menu, so the click that follows the
  // release does not also select the thread underneath.
  const suppressClick = useRef(false);

  const cancelHold = () => clearTimeout(holdTimer.current);

  // Desktop keeps the row clean: the "…" only appears on hover, when anything
  // in the row holds keyboard focus, while its menu is open, or on the row the
  // widget is currently in. Touch has no hover, so mobile always shows it.
  // `opacity-0` rather than `hidden` — the button keeps its 28px slot, so a row
  // never reflows as the pointer crosses it.
  const menuButtonVisibility =
    isMobile || isMenuOpen || isActive
      ? ''
      : 'opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 focus-visible:opacity-100';

  const startHold = (e: React.PointerEvent) => {
    if (e.pointerType === 'mouse') return;
    holdTimer.current = setTimeout(() => {
      suppressClick.current = true;
      onOpenMenu();
    }, LONG_PRESS_MS);
  };

  useEffect(() => cancelHold, []);

  return (
    <li className="relative" data-row-menu={isMenuOpen ? '' : undefined}>
      <div
        className={`group flex items-center gap-2.5 rounded-lg pl-3 pr-2 ${rowHeight} ${
          isActive ? 'bg-muted' : 'hover:bg-foreground/[0.04]'
        }`}
      >
        <button
          type="button"
          onClick={() => {
            if (suppressClick.current) {
              suppressClick.current = false;
              return;
            }
            onSelect();
          }}
          onPointerDown={startHold}
          onPointerUp={cancelHold}
          onPointerLeave={cancelHold}
          onPointerCancel={cancelHold}
          onContextMenu={(e) => isMobile && e.preventDefault()}
          className="flex min-w-0 flex-1 flex-col items-start gap-0.5 text-left"
        >
          <span
            className={`w-full truncate font-medium leading-[18px] text-foreground ${titleSize}`}
          >
            {thread.title || 'Untitled chat'}
          </span>
          <span className={`flex items-center gap-1 leading-4 text-muted-foreground ${metaSize}`}>
            {thread.pinned_at && <Pin className="h-3 w-3 shrink-0" aria-label="Pinned" />}
            {relativeTime(thread.last_message_at)}
          </span>
        </button>
        <button
          type="button"
          onClick={onToggleMenu}
          aria-label={`Options for ${thread.title || 'this chat'}`}
          aria-expanded={isMenuOpen}
          className={`flex shrink-0 items-center justify-center rounded-md text-muted-foreground transition-[opacity,color,background-color] duration-150 hover:bg-foreground/[0.06] hover:text-foreground ${
            isMobile ? 'h-11 w-11' : 'h-7 w-7'
          } ${menuButtonVisibility}`}
        >
          <MoreHorizontal className="h-4 w-4" />
        </button>
      </div>

      {isMenuOpen && (
        <div
          data-row-menu
          role="menu"
          className="absolute right-2 top-full z-10 w-[172px] rounded-xl border border-border bg-card p-1.5 shadow-[0_4px_16px_rgba(0,0,0,0.08),0_12px_32px_rgba(0,0,0,0.08)] dark:shadow-[0_4px_16px_rgba(0,0,0,0.4),0_12px_32px_rgba(0,0,0,0.3)]"
        >
          <MenuItem icon={Pencil} label="Rename" onClick={onRename} />
          <MenuItem
            icon={thread.pinned_at ? PinOff : Pin}
            label={thread.pinned_at ? 'Unpin' : 'Pin to top'}
            onClick={onTogglePin}
          />
          <div aria-hidden className="my-1 h-px bg-border" />
          <MenuItem icon={Trash2} label="Delete" onClick={onDelete} destructive />
        </div>
      )}
    </li>
  );
}

function MenuItem({
  icon: Icon,
  label,
  onClick,
  destructive,
}: {
  icon: typeof Pencil;
  label: string;
  onClick: () => void;
  destructive?: boolean;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className={`flex h-9 w-full items-center gap-2.5 rounded-lg px-2.5 text-sm leading-[18px] transition-colors hover:bg-foreground/[0.06] ${
        destructive ? 'text-destructive' : 'text-foreground'
      }`}
    >
      <Icon
        className={`h-4 w-4 shrink-0 ${destructive ? '' : 'text-muted-foreground'}`}
        strokeWidth={1.75}
      />
      {label}
    </button>
  );
}

export default WidgetHistoryView;
